import { useEffect, useState } from "react";

/** Outlasts the 240ms `label-ticker-*` animations in globals.css by a beat, so
 *  the outgoing value unmounts only after its exit has played. Keep this value
 *  and the CSS rules in sync by hand. */
export const TICKER_ANIMATION_MS = 280;

/**
 * Holds the outgoing value across a change so both halves of a ticker animate
 * at once (`label-ticker-exit` leaving, `label-ticker-enter` arriving), then
 * clears `previous` so only the current value stays mounted.
 *
 * `identity` gates the motion: a change under one identity swaps text in place
 * with nothing outgoing, e.g. `"reading a.ts L1-80"` → `"reading a.ts L81-160"`
 * both under `"reading a.ts"` never animates.
 */
export function useTickerTransition<T>(
  value: T,
  identity: unknown = value,
): {
  current: T;
  previous: T | null;
} {
  const [transition, setTransition] = useState<{
    current: T;
    identity: unknown;
    previous: T | null;
  }>({ current: value, identity, previous: null });

  if (identity !== transition.identity) {
    setTransition({ current: value, identity, previous: transition.current });
  } else if (value !== transition.current) {
    setTransition({ ...transition, current: value });
  }

  useEffect(() => {
    if (transition.previous === null) return;
    const timer = window.setTimeout(
      () => setTransition((current) => ({ ...current, previous: null })),
      TICKER_ANIMATION_MS,
    );
    return () => window.clearTimeout(timer);
  }, [transition.previous]);

  return { current: transition.current, previous: transition.previous };
}
